import { useState } from 'react';
import { ErrorState, Panel, Skeleton } from '@/design/components';
import { errorMessage } from '@/lib/errors';
import { SurveyBuilder } from './SurveyBuilder';
import { draftFromSurvey, type SurveyDraft } from './draft';
import { useSurveyForEdit, useSurveyMutations, type SurveyForEdit } from './api';

/**
 * עריכת סקר קיים.
 *
 * הטעינה והטיוטה יושבות כאן ולא במסך שמעל, כדי שהטיוטה תיווצר
 * פעם אחת מהסקר שנטען ולא תידרס בכל רינדור. Loaded מקבל key לפי
 * מזהה הסקר, ולכן מעבר מסקר לסקר מתחיל טיוטה נקייה בלי אפקט.
 */
export function EditSurveyPanel({
  surveyId,
  onDone,
  onError,
}: {
  surveyId: string;
  onDone: () => void;
  onError: (message: string | null) => void;
}) {
  const survey = useSurveyForEdit(surveyId);

  if (survey.isPending) return <Skeleton className="h-64" />;
  if (survey.isError) return <ErrorState onRetry={() => void survey.refetch()} />;
  if (!survey.data) return null;

  return <Loaded key={surveyId} survey={survey.data} onDone={onDone} onError={onError} />;
}

function Loaded({
  survey,
  onDone,
  onError,
}: {
  survey: SurveyForEdit;
  onDone: () => void;
  onError: (message: string | null) => void;
}) {
  const { update } = useSurveyMutations();
  const [draft, setDraft] = useState<SurveyDraft>(() => draftFromSurvey(survey));

  return (
    <Panel title={`עריכת ${survey.title}`} className="overflow-visible">
      <SurveyBuilder
        draft={draft}
        onChange={setDraft}
        locked={survey.has_responses}
        saving={update.isPending}
        onCancel={onDone}
        onSubmit={() => {
          onError(null);
          update
            .mutateAsync({
              id: survey.id,
              title: draft.title,
              description: draft.description.trim() || null,
              analysis_note: draft.analysis_note.trim() || null,
              is_anonymous: draft.is_anonymous,
              starts_at: draft.starts_at ? new Date(draft.starts_at).toISOString() : null,
              ends_at: draft.ends_at ? new Date(draft.ends_at).toISOString() : null,
              questions: draft.questions,
            })
            .then(onDone)
            .catch((caught: unknown) => onError(errorMessage(caught)));
        }}
      />
    </Panel>
  );
}
